import {
  Badge,
  Box,
  Editable,
  Flex,
  HStack,
  IconButton,
} from "@chakra-ui/react"
import { type ReactNode, memo } from "react"
import {
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiMenu,
  FiTrash,
  FiTrash2,
  FiX,
} from "react-icons/fi"
import type { QuizSection } from "./quiz-builder-types"

interface SectionCardProps {
  section: QuizSection
  onToggleCollapse: (sectionId: string) => void
  onUpdateName: (sectionId: string, newName: string) => void
  onDelete: (sectionId: string) => void
  onClearQuestions?: (sectionId: string) => void
  showDelete?: boolean
  dragHandleProps?: object
  children?: ReactNode
}

export const SectionCard = memo(function SectionCard({
  section,
  onToggleCollapse,
  onUpdateName,
  onDelete,
  onClearQuestions,
  showDelete = true,
  dragHandleProps,
  children,
}: SectionCardProps) {
  const questionCount = section.questions.length
  const isCollapsed = section.isCollapsed ?? false

  const handleNameSubmit = (value: string) => {
    const trimmed = value.trim()
    if (trimmed && trimmed !== section.name) {
      onUpdateName(section.id, trimmed)
    }
  }

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      {/* Section Header */}
      <Flex align="center" justify="space-between" p={3} bg="bg.subtle" gap={3}>
        <HStack gap={2} flex="1" minW={0}>
          {/* Drag Handle */}
          <Box
            {...dragHandleProps}
            cursor="grab"
            _active={{ cursor: "grabbing" }}
            color="fg.muted"
          >
            <FiMenu />
          </Box>

          {/* Collapse Button */}
          <IconButton
            aria-label={isCollapsed ? "Expand section" : "Collapse section"}
            variant="ghost"
            size="sm"
            onClick={() => onToggleCollapse(section.id)}
          >
            {isCollapsed ? <FiChevronRight /> : <FiChevronDown />}
          </IconButton>

          {/* Editable Section Name */}
          <Editable.Root
            key={`${section.id}-${section.name}`}
            defaultValue={section.name}
            onValueCommit={(event) => handleNameSubmit(event.value)}
            flex="1"
            minW={0}
          >
            <Editable.Preview
              fontWeight="semibold"
              fontSize="md"
              cursor="pointer"
              _hover={{ bg: "bg.muted" }}
              px={2}
              py={1}
              borderRadius="sm"
              lineClamp={1}
            />
            <Editable.Input px={2} py={1} />
            <Editable.Control>
              <Editable.EditTrigger asChild>
                <IconButton
                  aria-label="Edit section name"
                  variant="ghost"
                  size="sm"
                >
                  <FiEdit2 />
                </IconButton>
              </Editable.EditTrigger>
              <Editable.CancelTrigger asChild>
                <IconButton
                  aria-label="Cancel section rename"
                  variant="ghost"
                  size="sm"
                >
                  <FiX />
                </IconButton>
              </Editable.CancelTrigger>
              <Editable.SubmitTrigger asChild>
                <IconButton
                  aria-label="Confirm section rename"
                  variant="solid"
                  size="sm"
                >
                  <FiCheck />
                </IconButton>
              </Editable.SubmitTrigger>
            </Editable.Control>
          </Editable.Root>

          {/* Question Count Badge */}
          <Badge colorPalette="blue" variant="subtle">
            {questionCount} {questionCount === 1 ? "question" : "questions"}
          </Badge>
        </HStack>

        {/* Clear All Questions Button */}
        {onClearQuestions && questionCount > 0 && (
          <IconButton
            aria-label="Delete all questions in section"
            title="Delete all questions in this section"
            variant="ghost"
            colorPalette="orange"
            size="sm"
            onClick={() => onClearQuestions(section.id)}
          >
            <FiTrash2 />
          </IconButton>
        )}

        {/* Delete Button */}
        {showDelete && (
          <IconButton
            aria-label="Delete section"
            variant="ghost"
            colorPalette="red"
            size="sm"
            onClick={() => onDelete(section.id)}
          >
            <FiTrash />
          </IconButton>
        )}
      </Flex>

      {/* Section Content (Questions) */}
      {!isCollapsed && children && (
        <Box p={3} bg="bg.canvas">
          {children}
        </Box>
      )}
    </Box>
  )
})
